import { ArrowRight, CheckCircle, Circle, IdentificationCard } from "@phosphor-icons/react";
import { FC } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  KycRequirement,
  KycRequirementKey,
  KycRequirementsResult,
} from "../hooks/useKycRequirements";

/**
 * Where a customer goes to satisfy each requirement.
 *
 * `email`, `name` and `date_of_birth` are collected during signup and cannot
 * realistically be outstanding here, so they route to the profile screen rather
 * than getting bespoke destinations.
 */
const ROUTES: Record<KycRequirementKey, string> = {
  email: "/home/settings/verification",
  name: "/home/settings/profile",
  date_of_birth: "/home/settings/profile",
  nin: "/home/settings/compliance",
  address: "/home/settings/compliance/home-address",
  passport: "/home/settings/compliance/travel-document",
  residence: "/home/settings/compliance/residence",
};

/**
 * Blocks Pay in 3 until the customer has the KYC required for flight credit,
 * and links each gap to the screen that fills it.
 *
 * Presentational only. The real enforcement is server-side in createOrder,
 * which re-checks the same requirements before a financing request is sent.
 * Wallet payment remains outside this gate.
 */
const IntlKycGate: FC<{
  requirements: KycRequirementsResult;
  onRecheck: () => void;
  onLeave?: () => void;
}> = ({ requirements, onRecheck, onLeave }) => {
  const navigate = useNavigate();

  const outstanding = requirements.requirements.filter((r) => !r.satisfied);
  const satisfied = requirements.requirements.filter((r) => r.satisfied);

  const Row: FC<{ requirement: KycRequirement; done: boolean }> = ({
    requirement,
    done,
  }) => (
    <button
      type="button"
      disabled={done}
      onClick={() => {
        onLeave?.();
        navigate(ROUTES[requirement.key] ?? "/home/settings/compliance", {
          state: { returnTo: window.location.pathname },
        });
      }}
      className={`flex items-start gap-3 w-full text-left rounded-xl border p-4 transition-colors ${
        done
          ? "border-gray-100 bg-gray-50 cursor-default"
          : "border-gray-200 hover:border-visaro-orange hover:bg-visaro-orange/5"
      }`}
    >
      {done ? (
        <CheckCircle weight="fill" className="size-5 shrink-0 mt-0.5 text-green-600" />
      ) : (
        <Circle className="size-5 shrink-0 mt-0.5 text-gray-300" />
      )}
      <span className="flex-1 min-w-0">
        <span className={`block font-bold text-sm ${done ? "text-gray-400" : "text-deepBlue"}`}>
          {requirement.label}
        </span>
        <span className="block text-sm text-gray-500 mt-0.5">
          {requirement.description}
        </span>
      </span>
      {!done && <ArrowRight className="size-4 shrink-0 mt-1 text-gray-400" />}
    </button>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      className="bg-white rounded-2xl shadow-[0_4px_20px_-4px_rgba(15,23,42,0.10)] border border-gray-100 p-6 flex flex-col gap-5"
    >
      <div className="flex items-start gap-3">
        <span className="flex items-center justify-center size-10 rounded-full bg-visaro-orange/10 text-visaro-orange shrink-0">
          <IdentificationCard size={20} weight="fill" />
        </span>
        <div className="flex flex-col gap-1">
          <h2 className="font-bold text-lg text-deepBlue font-aeonic">
            Complete your KYC to use Pay in 3
          </h2>
          <p className="text-sm text-gray-500">
            These checks are required for flight financing. You can choose wallet payment without them.
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        {outstanding.map((requirement) => (
          <Row key={requirement.key} requirement={requirement} done={false} />
        ))}
      </div>

      {satisfied.length > 0 && (
        <details className="text-sm">
          <summary className="text-gray-500 cursor-pointer select-none">
            {satisfied.length} already on file
          </summary>
          <div className="flex flex-col gap-3 mt-3">
            {satisfied.map((requirement) => (
              <Row key={requirement.key} requirement={requirement} done />
            ))}
          </div>
        </details>
      )}

      <button
        type="button"
        onClick={onRecheck}
        className="text-sm font-medium text-visaro-orange self-start"
      >
        I have added these — check again
      </button>
    </motion.div>
  );
};

export default IntlKycGate;
